import React from 'react';
import { Tooltip } from '@mui/material';

import { classNames }  from '../lib/classNames';

import { SmallSpinner } from './SmallSpinner';
import { Transition } from './Transition';

const colors = {
  'paidniceGreen': `bg-paidniceGreen-600 hover:bg-paidniceGreen-700 focus-visible:ring-paidniceGreen-500 text-white`,
  'paidniceGreenOutline': `bg-transparent border-paidniceGreen-700 hover:bg-paidniceGreen-700 focus-visible:ring-paidniceGreen-500 text-paidniceGreen-600 hover:text-white`,
  'orangeOutline': `bg-transparent border-orange-500 hover:bg-orange-500 focus-visible:ring-orange-500 text-orange-500 hover:text-white`,
  'red': `bg-red-800 hover:bg-red-900 dark:bg-[#991b1b] dark:hover:bg-[#b91c1c] focus-visible:ring-red-800 text-white`,
  'gray': `bg-gray-200 hover:bg-gray-300 focus-visible:ring-gray-500 text-paidniceGreen-800`,
  'green': `bg-green-500 hover:bg-green-700 dark:hover:bg-[#15803d] focus-visible:ring-green-500 text-white`,
  'greenOutline': `bg-transparent border-green-500 hover:bg-green-700 dark:hover:bg-[#15803d] focus-visible:ring-green-500 text-green-500 hover:text-white`,
  'orange': `bg-orange-500 hover:bg-orange-700 dark:hover:bg-[#c2410c] focus-visible:ring-orange-500 text-white`,
  'purple': `bg-purple-700 hover:bg-purple-800 dark:bg-[#7e22ce] dark:hover:bg-[#6b21a8] focus-visible:ring-purple-700 text-white`,
  'paidniceRed': `bg-paidniceRed-600 hover:bg-paidniceRed-700 focus-visible:ring-paidniceRed-500 text-white`,
  'paidniceRedOutline': `bg-transparent border-paidniceRed-700 hover:bg-paidniceRed-700 focus-visible:ring-paidniceRed-500 text-paidniceRed-600 hover:text-white`,
  'white': `bg-white border-gray-300 hover:bg-gray-50 focus-visible:ring-paidniceGreen-500 text-gray-700`,
  'ghost': `bg-transparent shadow-none text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus-visible:ring-paidniceGreen-500`,
};

const sizes = {
  'sm': `px-3 py-1.5 text-xs max-h-6`,
  's': `px-3.5 py-2 text-xs max-h-6`,
  'md': `px-3.5 py-2 text-sm leading-4`,
  'lg': `px-4 py-2 text-sm`,
  'xl': `px-5 py-2 text-base`,
  '2xl': `px-6 py-3 text-base`,
  '3xl': `px-8 py-6 text-lg`,
};

const base = [
  `inline-flex items-center max-h-[32px] whitespace-nowrap text-center font-medium cursor-pointer select-none`,
  `border border-transparent shadow-xs`,
  `transition-[color,background-color,border-color,box-shadow,transform] duration-150 ease-out enabled:active:scale-[0.97]`,
  `focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2`,
].join(` `);

export type ButtonColorOpts = `paidniceGreen` | `red` | `gray` | `none` | `paidniceRed` | `green` | `orange` | `purple` | `paidniceGreenOutline` | `orangeOutline` | `greenOutline` | `paidniceRedOutline` | `white` | `ghost`;

interface IButtonProps extends React.ComponentProps<`button`> {
  loadingText?: string;
  active?: boolean;
  tooltip?: string;
  icon?: React.ReactNode;
  loading?: boolean;
  color?: ButtonColorOpts;
  size?: keyof typeof sizes;
  width?: string;
  isIcon?: boolean;
}

export function generateButtonDefaultClassnames(size: IButtonProps[`size`], color: IButtonProps[`color`]) {
  return classNames(base, sizes[size], colors[color]);
}

const roundedClass = /\brounded(-\S+)?\b/;

export function Button({
  loadingText,
  active,
  tooltip,
  icon,
  disabled,
  loading,
  className,
  color = `paidniceGreen`,
  children,
  size = `md`,
  width,
  isIcon,
  style,
  ...props
}: IButtonProps) {
  return (
    <Tooltip title={ tooltip }>
      <span>
        <button
          type={ `button` }
          style={ width ? { ...style, width } : style }
          aria-busy={ loading || undefined }
          className={ classNames(
            generateButtonDefaultClassnames(size, color),
            !roundedClass.test(className ?? ``) && `rounded-lg`,
            className,
            disabled && `opacity-50 cursor-not-allowed`,
            active && `bg-paidniceGreen-700`,
            isIcon && `p-0`,
          ) }
          { ...props }
          disabled={ disabled }
        >
          <Transition show={ !!loading }>
            <div>
              <SmallSpinner className={ `mr-2` }
                colored={ [`white`, `ghost`, `gray`].includes(color) || color.endsWith(`Outline`) } />
            </div>
          </Transition>
          { loading && loadingText ? loadingText : children }
          <Transition show={ !!icon }>
            <div>
              { icon }
            </div>
          </Transition>
        </button>
      </span>
    </Tooltip>
  );
}
